/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */
import { FlowIfEnabled } from '@/components/blocks/FlexFlow';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { GDPRDialog } from '@/components/content/Footer/parts/GDPRDialog';
import { footerContainerSX } from '@/components/content/Footer/styles/container';
import { useModalPrivacyPolicy } from '@/data/Content/ModalPrivacyPolicy';
import { dFix, useSettings } from '@/data/Settings';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { ID } from '@/data/types/Basic';
import { Box, Container, Paper } from '@mui/material';
import { FC, useContext, useEffect } from 'react';
import { footerLinksContainerSX, footerNavbarSX } from './styles/footerNavbar';
import { useUser } from '@/data/User';
import { CommonDataDelegatorProps, PageDataDelegatorProps, UserDataDelegatorProps } from '@/data/events/delegators/GACommonData';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { EventsContext } from '@/data/context/events';
import { waitForElement } from '@/utils/waitForElementToLoad';
import { useDeviceType } from '@/data/deviceDetection';

export const Footer: FC<{
	id: ID;
	clickAction?: () => void;
}> = ({ id }) => {
	const {isMobile} = useDeviceType();
	const { settings } = useSettings();
	const { data, loading, onSubmit, privacyNoticeVersion, privacyNoticeVersionValue } =
		useModalPrivacyPolicy();
	const { user } = useUser();

	const pageDataValue = usePageDataFromId(); 
	const router = useNextRouter();
	const { onCommonView, onXPageView, onUserData } = useContext(EventsContext);
	
	const { locale } = router;
	
	useEffect(() => {
		if (pageDataValue.data && settings) {
			const commonData: CommonDataDelegatorProps = {
				gtm: {
					storeId: settings.storeId,
					country: "NL", //TBD Read from somewhere
					currency: settings.defaultCurrency,
					environment: settings.userData.GTMEnvironment,
					locale: locale ?? "nl-NL",
					language: settings.defaultLanguage,
					device: isMobile ? "mobile" : "desktop",
					settings,
				}
			};
			const pageData: PageDataDelegatorProps = {
				gtm: {
					pageName: pageDataValue.data?.page.title ?? "",
					pageType: pageDataValue.data?.page.type ?? "",
					settings,
				}
			};
			const userData: UserDataDelegatorProps = {
				gtm: {
					userId: user?.userId ?? "-1002",
					userType: "", // TBD Determine Type. user?.nothingUsefulExistsNow,
					settings,
				}
			};
			onCommonView(commonData);
			onUserData(userData);
			onXPageView(pageData);
		}
	}, [pageDataValue, settings, onUserData]);

	useEffect(() => {
		if (isMobile) {
			let headerElems: NodeListOf<Element> | null = null;
			waitForElement('.footer-links-header', 4000, 'footer').then(function () {
				headerElems = document.querySelectorAll(".footer-links-header");
				[...headerElems].forEach(elem => {
					elem.addEventListener('click', handleLinkHeaderClick, false);
				});
			}).catch(() => {
				console.debug("Element footer-links-header did not load in 4 seconds")
			});

			const handleLinkHeaderClick = (event: any) => {
				if (headerElems) {
					[...headerElems].forEach(elem => {
						elem.classList.remove("selected");
						if (event.target !== elem) {
							elem.nextElementSibling?.classList.add("hide-mobile");
						}
					});
					const targetList = event.target.nextElementSibling;
					if (targetList && targetList.classList) {
						if ([...targetList.classList].includes("hide-mobile")) {
							targetList.classList.remove("hide-mobile");
							targetList.classList.add("selected");
							event.target.classList.add("selected");
							event.target.scrollIntoView(true, { behavior: "smooth" });
							window.scrollBy(0, -150);
						} else {
							targetList.classList.add("hide-mobile");
						}
					}
				}
			};
			return () => {
				if (headerElems) {
					[...headerElems].forEach(elem => {
						elem.removeEventListener('click', handleLinkHeaderClick, false);
					});
				}
			};
		}
	}, [isMobile]);

	const acceptedSessionPrivacyPolicy =
		dFix(privacyNoticeVersion ?? '') === dFix(privacyNoticeVersionValue ?? '');

	return (
		<Paper
			component="footer"
			sx={footerContainerSX(settings?.csrSession)}
			{...(settings?.csrSession && { 'data-iframe-height': '' })}
		>
			<Box sx={footerNavbarSX}>
				<Container>
					<ContentRecommendation
						id='IT-Footer-NewsLetter-eSpot_rv'
						properties={{emsName:'IT-Footer-NewsLetter-eSpot_rv'}}
					/>
				</Container>
			</Box>
			<Box>
				<Container>
					<Box sx={footerLinksContainerSX}>
						<ContentRecommendation
							id='IT-Footer-Links-eSpot_rv'
							properties={{emsName:'IT-Footer-Links-eSpot_rv'}}
						/>
					</Box>
				</Container>
			</Box>
			<Box>
				<Container>
					<Box>
						<ContentRecommendation
							id='IT-Footer-Payment-eSpot_rv'
							properties={{emsName:'IT-Footer-Payment-eSpot_rv'}}
						/>
					</Box>
				</Container>
				{!acceptedSessionPrivacyPolicy ? (
					<FlowIfEnabled feature={EMS_STORE_FEATURE.CONSENT_OPTIONS}>
						{loading ? <ProgressIndicator /> : <GDPRDialog data={data} onSubmit={onSubmit} />}
					</FlowIfEnabled>
			) : null}
			</Box>
		</Paper>
	);
};